export type RecommendedTimezone = {
    city: string;
    countryCode: 'ae' | 'ch' | 'cn' | 'de' | 'fr' | 'gb' | 'hk' | 'ie' | 'jp' | 'kr' | 'sg' | 'us';
    label: string;
    timeZone: string;
};

export const FINANCIAL_HUB_TIMEZONES: RecommendedTimezone[] = [
    { city: 'New York', countryCode: 'us', label: 'New York', timeZone: 'America/New_York' },
    { city: 'Dublin', countryCode: 'ie', label: 'Dublin', timeZone: 'Europe/Dublin' },
    { city: 'London', countryCode: 'gb', label: 'London', timeZone: 'Europe/London' },
    { city: 'Paris', countryCode: 'fr', label: 'Paris', timeZone: 'Europe/Paris' },
    { city: 'Geneva', countryCode: 'ch', label: 'Geneva', timeZone: 'Europe/Zurich' },
    { city: 'Frankfurt', countryCode: 'de', label: 'Frankfurt', timeZone: 'Europe/Berlin' },
    { city: 'Dubai', countryCode: 'ae', label: 'Dubai', timeZone: 'Asia/Dubai' },
    { city: 'Hong Kong', countryCode: 'hk', label: 'Hong Kong', timeZone: 'Asia/Hong_Kong' },
    { city: 'Singapore', countryCode: 'sg', label: 'Singapore', timeZone: 'Asia/Singapore' },
    { city: 'Shanghai', countryCode: 'cn', label: 'Shanghai', timeZone: 'Asia/Shanghai' },
    { city: 'Seoul', countryCode: 'kr', label: 'Seoul', timeZone: 'Asia/Seoul' },
    { city: 'Tokyo', countryCode: 'jp', label: 'Tokyo', timeZone: 'Asia/Tokyo' },
];

export const getBrowserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return undefined;
    }
};

export const getSupportedTimezones = () => {
    const supportedValuesOf = Intl as typeof Intl & {
        supportedValuesOf?: (key: 'timeZone') => string[];
    };
    const filterUserFacingTimezones = (timeZones: string[]) =>
        timeZones.filter(
            (timeZone) =>
                timeZone !== 'Factory' &&
                !timeZone.startsWith('Etc/') &&
                !timeZone.startsWith('SystemV/') &&
                !timeZone.startsWith('US/'),
        );

    if (supportedValuesOf.supportedValuesOf) {
        return filterUserFacingTimezones(supportedValuesOf.supportedValuesOf('timeZone'));
    }

    return FINANCIAL_HUB_TIMEZONES.map(({ timeZone }) => timeZone);
};

export const formatTimezoneLabel = (timeZone: string) => {
    const city = timeZone.split('/').at(-1) ?? timeZone;

    return city.replaceAll('_', ' ');
};

export const formatTimezoneRegion = (timeZone: string) => {
    const segments = timeZone.split('/');
    const region = segments.length > 2 ? segments.slice(0, -1).join(' / ') : segments[0];

    return region?.replaceAll('_', ' ') ?? timeZone;
};

export const formatTimezoneCurrentTime = (timeZone: string, date = new Date()) => {
    try {
        return date.toLocaleTimeString('en-US', {
            timeZone,
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
        });
    } catch {
        return '';
    }
};

const ordinalDay = (day: number) => {
    const remainder = day % 100;

    if (remainder >= 11 && remainder <= 13) {
        return `${day}th`;
    }

    switch (day % 10) {
        case 1:
            return `${day}st`;
        case 2:
            return `${day}nd`;
        case 3:
            return `${day}rd`;
        default:
            return `${day}th`;
    }
};

/** Formats a timestamp in the user's preferred timezone for compact UI tooltips. */
export const formatPreferredTimezoneDateTime = (
    value: Date | number | string,
    preferredTimezone?: string,
    displayOptions?: { month?: 'long' | 'short'; weekday?: 'long' | 'short' },
): string => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const timeZone = preferredTimezone ?? getBrowserTimezone();
    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        hour: '2-digit',
        hour12: true,
        minute: '2-digit',
        month: displayOptions?.month ?? 'long',
        timeZone,
        timeZoneName: 'short',
        weekday: displayOptions?.weekday,
        year: 'numeric',
    };

    try {
        const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
        const getPart = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
        const day = Number(getPart('day'));
        const weekday = getPart('weekday');
        const formattedDate = `${getPart('month')} ${ordinalDay(day)}${weekday ? ' ' : ', '}${getPart('year')}`;

        return `${weekday ? `${weekday}, ` : ''}${formattedDate} at ${getPart('hour')}:${getPart(
            'minute',
        )} ${getPart('dayPeriod')} ${getPart('timeZoneName')}`.trim();
    } catch {
        return timeZone === 'UTC' ? date.toISOString() : formatPreferredTimezoneDateTime(date, 'UTC', displayOptions);
    }
};

export const buildRecommendedTimezones = (options?: { browserTimezone?: string; preferredTimezone?: string }) => {
    const recommendations: RecommendedTimezone[] = [];
    const addRecommendation = (recommendation: RecommendedTimezone | undefined) => {
        if (!recommendation) {
            return;
        }
        if (recommendations.some(({ timeZone }) => timeZone === recommendation.timeZone)) {
            return;
        }

        recommendations.push(recommendation);
    };

    if (options?.preferredTimezone) {
        addRecommendation({
            city: formatTimezoneLabel(options.preferredTimezone),
            countryCode: 'us',
            label: 'Preferred',
            timeZone: options.preferredTimezone,
        });
    }

    if (options?.browserTimezone && options.browserTimezone !== options.preferredTimezone) {
        addRecommendation({
            city: formatTimezoneLabel(options.browserTimezone),
            countryCode: 'us',
            label: 'Local',
            timeZone: options.browserTimezone,
        });
    }

    FINANCIAL_HUB_TIMEZONES.forEach(addRecommendation);

    return recommendations;
};
